import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, Subject } from 'rxjs';
import { DisabilityMemoData } from './dental-disability-memo.component';
import { PatientApi, PatientDisabilityDto } from 'src/app/services/api-service/patient/patient-api';

/**
 * 優化過的輸出格式（用於保存和傳輸）
 */
export interface DentalDisabilityFormDataOptimized {
  patientNo: string;                                // 患者編號
  patientName: string;                              // 患者姓名
  patientCId?: string;                              // 患者身分證號
  disabilityCategories?: string;                    // 新制類別 ID 字串，型如 "cat1, cat2, cat3, ..."
  disabilityScores?: string;                        // 舊制代碼+嚴重度字串，型如 "06.1, 09.2, 10.3, ..."
  specialRequirements?: string;                     // 特殊需求字串，型如 "companion, extended_time"
  leftImageUrl?: string;                            // 左側影像 URL
  rightImageUrl?: string;                           // 右側影像 URL
  modifiedDate?: string;                            // 修改日期 ISO，型如 "2026-01-23T22:26:38.692Z"
  matchedDisabilityTypeCodes?: string;              // 換算後的障礙別代碼字串，型如 "A, B, C, D, E, ..."
  computedMultipleDisabilityScore?: string | null;  // 多重障礙換算，型如 "13.4"
  selectedServiceMode?: string;                     // 服務模式，型如 "in-clinic"
  matchedTreatmentItem?: string;                    // 換算後的治療項目代碼，型如 "FC"
}

/**
 * 完整的表單數據結構（包含計算結果）
 */
export interface DentalDisabilityFormData extends DisabilityMemoData {
  // 基本資訊已在 DisabilityMemoData 中定義
  // 新增計算結果相關欄位
  matchedTreatmentItems?: string[]; // 符合的治療項目代號
  matchedDisabilityTypeCodes?: string[]; // 符合的障礙別代碼(A-Q)
  selectedServiceMode?: string; // 選擇的服務模式
  quickSelectTags?: Array<{ display: string; code: string; severity: string }>; // 快選標籤
  computedMultipleDisabilityScore?: { code: string; severity: 'mild' | 'moderate' | 'severe' | 'profound' | null } | null; // 計算出的多重障礙嚴重度
}

@Injectable({
  providedIn: 'root'
})
export class DentalDisabilityMemoService {

  private apiUrl = '/api/dental-disability-memo';
  private memoDataSubject = new BehaviorSubject<DisabilityMemoData | null>(null);
  public memoData$ = this.memoDataSubject.asObservable();

  // 完整表單數據（包含計算結果）
  private formDataSubject = new BehaviorSubject<DentalDisabilityFormData | null>(null);
  public formData$ = this.formDataSubject.asObservable();

  // 優化過的輸出格式（用於保存和傳輸）
  private formDataOptimizedSubject = new BehaviorSubject<DentalDisabilityFormDataOptimized | null>(null);
  public formDataOptimized$ = this.formDataOptimizedSubject.asObservable();

  // 表單儲存事件（僅在使用者按下確定時觸發，不含 storage 還原）
  private formSavedSubject = new Subject<DentalDisabilityFormDataOptimized>();
  public formSaved$ = this.formSavedSubject.asObservable();

  // localStorage key prefix（依患者編號區分）
  private readonly STORAGE_PREFIX = 'dentalDisMemo_';
  private readonly STORAGE_PREFIX_OPTIMIZED = 'dentalDisMemoOpt_';
  // 目前患者編號
  private currentPatientNo: string = '';

  constructor(
    private http: HttpClient,
    private patientApi: PatientApi
  ) {
    // 不自動還原——需在載入病歷時手動呼叫 loadForPatient()
    // NOTE: 計算邏輯已整合至 DentalAdditionApiService
    //       本 service 的 calculateExamFeeMultiplier / calculateTreatmentMultiplier 保留向下相容
    //       但內部邏輯與 DentalAdditionApiService 重複，建議外部呼叫者改用 DentalAdditionApiService
  }

  /**
   * 根據患者編號獲取身障註記
   */
  getMemoByPatientNo(patientNo: string): Observable<DisabilityMemoData> {
    return this.http.get<DisabilityMemoData>(`${this.apiUrl}/${patientNo}`);
  }

  /**
   * 保存或更新身障註記
   */
  saveMemo(memoData: DisabilityMemoData): Observable<DisabilityMemoData> {
    if (memoData.createdDate) {
      // Update existing
      return this.http.put<DisabilityMemoData>(
        `${this.apiUrl}/${memoData.patientNo}`,
        memoData
      );
    } else {
      // Create new
      return this.http.post<DisabilityMemoData>(this.apiUrl, memoData);
    }
  }

  /**
   * 刪除身障註記
   */
  deleteMemo(patientNo: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${patientNo}`);
  }

  /**
   * 更新本地記憶體中的註記數據
   */
  updateLocalMemoData(memoData: DisabilityMemoData): void {
    this.memoDataSubject.next(memoData);
  }

  /**
   * 取得本地記憶體中的註記數據
   */
  getLocalMemoData(): DisabilityMemoData | null {
    return this.memoDataSubject.value;
  }

  /**
   * 清除本地記憶體中的註記數據
   */
  clearLocalMemoData(): void {
    this.memoDataSubject.next(null);
  }

  /**
   * 更新完整的表單數據（包含計算結果）
   * @param formData 完整的表單數據
   * @param saveToStorage 是否同時保存到 localStorage（默認為 true）
   */
  updateFormData(formData: DentalDisabilityFormData, saveToStorage: boolean = true): void {
    this.formDataSubject.next(formData);
    if (saveToStorage) {
      this.saveFullToLocalStorage(formData);
    }
  }

  /**
   * 取得完整的表單數據
   */
  getFormData(): DentalDisabilityFormData | null {
    return this.formDataSubject.value;
  }

  /**
   * 取得完整的表單數據 JSON 字串
   */
  getFormDataAsJson(): string | null {
    const formData = this.formDataSubject.value;
    return formData ? JSON.stringify(formData, null, 2) : null;
  }

  /**
   * 清除完整的表單數據
   */
  clearFormData(): void {
    this.formDataSubject.next(null);
    this.clearLocalStorage();
  }

  /**
   * 保存完整格式到 localStorage（依患者編號）
   */
  private saveFullToLocalStorage(formData: DentalDisabilityFormData): void {
    const patNo = (formData as any).patientNo || this.currentPatientNo;
    if (!patNo) return;
    try {
      localStorage.setItem(this.STORAGE_PREFIX + patNo, JSON.stringify(formData));
    } catch (error) {
      console.error('Failed to save full form data to localStorage:', error);
    }
  }

  /**
   * 從 localStorage 恢復完整格式
   */
  private restoreFullFromLocalStorage(patientNo: string): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_PREFIX + patientNo);
      if (stored) {
        const formData = JSON.parse(stored) as DentalDisabilityFormData;
        this.formDataSubject.next(formData);
      } else {
        this.formDataSubject.next(null);
      }
    } catch (error) {
      console.error('Failed to restore full form data from localStorage:', error);
      this.formDataSubject.next(null);
    }
  }

  /**
   * 更新優化過的表單數據（簡化的 JSON 格式）
   * 會同時保存到 localStorage 並觸發 formSaved$ 事件
   */
  updateFormDataOptimized(formData: DentalDisabilityFormDataOptimized): void {
    this.currentPatientNo = formData.patientNo || this.currentPatientNo;
    this.formDataOptimizedSubject.next(formData);
    this.formSavedSubject.next(formData);
    this.saveOptimizedToLocalStorage(formData);
  }

  /**
   * 取得優化過的表單數據
   */
  getFormDataOptimized(): DentalDisabilityFormDataOptimized | null {
    return this.formDataOptimizedSubject.value;
  }

  hasFormDataOptimizedForPatient(patientNo: string): boolean {
    const formData = this.formDataOptimizedSubject.value;
    return !!(patientNo && formData && formData.patientNo === patientNo && this.hasOptimizedData(formData));
  }

  /**
   * 取得優化過的表單數據 JSON 字串
   */
  getFormDataOptimizedAsJson(): string | null {
    const formData = this.formDataOptimizedSubject.value;
    return formData ? JSON.stringify(formData, null, 2) : null;
  }

  /**
   * 保存優化格式到 localStorage（依患者編號）
   */
  async loadFromDatabase(
    patientId: number,
    patientNo: string = '',
    patientName: string = '',
    patientCId: string = '',
    emitFormSaved: boolean = false
  ): Promise<boolean> {
    if (!patientId) {
      this.formDataSubject.next(null);
      this.formDataOptimizedSubject.next(null);
      return false;
    }

    let record: PatientDisabilityDto;
    try {
      record = await this.patientApi.GetPatientDisability(patientId);
    } catch (error) {
      console.error('[dental-disability-memo] failed to load PatientDisability.', error);
      this.formDataSubject.next(null);
      this.formDataOptimizedSubject.next(null);
      return false;
    }

    const recordPatientId = this.getModelValue<number>(record, 'PatientId');
    if (!record || !recordPatientId) {
      this.formDataSubject.next(null);
      this.formDataOptimizedSubject.next(null);
      return false;
    }

    const optimized = this.mapPatientDisabilityToOptimized(record, patientNo, patientName, patientCId);
    const fullData = this.mapOptimizedToFullFormData(optimized, record);

    this.currentPatientNo = optimized.patientNo || patientNo || this.currentPatientNo;
    this.formDataOptimizedSubject.next(optimized);
    this.formDataSubject.next(fullData);

    if (this.currentPatientNo) {
      this.clearLocalStorageForPatient(this.currentPatientNo);
    }

    const hasData = this.hasOptimizedData(optimized);
    if (hasData && emitFormSaved) {
      this.formSavedSubject.next({ ...optimized });
    }

    return true;
  }

  async saveToDatabase(patientId: number, formData: DentalDisabilityFormDataOptimized): Promise<any> {
    if (!patientId) {
      throw new Error('patientId is required to save dental disability data.');
    }

    const payload = this.toPatientDisabilityDto(patientId, formData);
    const result = await this.patientApi.SavePatientDisability(payload);
    this.clearLocalStorageForPatient(payload.PatientNo);
    return result;
  }

  private toPatientDisabilityDto(patientId: number, formData: DentalDisabilityFormDataOptimized): PatientDisabilityDto {
    return {
      PatientId: patientId,
      PatientNo: formData.patientNo || this.currentPatientNo || '',
      DisabilityCategories: this.trimToColumn(formData.disabilityCategories, 80),
      DisabilityScores: this.trimToColumn(formData.disabilityScores, 180),
      SpecialRequirements: this.trimToColumn(formData.specialRequirements, 100),
      ImageUrl: this.buildPersistedImageUrl(formData),
      MatchedDisabilityTypeCodes: this.trimToColumn(formData.matchedDisabilityTypeCodes, 40),
      ComputedMultipleDisabilityScore: this.trimToColumn(formData.computedMultipleDisabilityScore || '', 10),
      Registeries: this.buildRegisteries(formData)
    };
  }

  private mapPatientDisabilityToOptimized(
    record: PatientDisabilityDto,
    patientNo: string,
    patientName: string,
    patientCId: string
  ): DentalDisabilityFormDataOptimized {
    const meta = this.parseRegisteries(this.getModelValue<string>(record, 'Registeries'));
    const imageUrls = this.parsePersistedImageUrl(this.getModelValue<string>(record, 'ImageUrl'));
    return {
      patientNo: this.getModelValue<string>(record, 'PatientNo') || patientNo || '',
      patientName: patientName || '',
      patientCId: patientCId || '',
      disabilityCategories: this.getModelValue<string>(record, 'DisabilityCategories') || '',
      disabilityScores: this.getModelValue<string>(record, 'DisabilityScores') || '',
      specialRequirements: this.getModelValue<string>(record, 'SpecialRequirements') || '',
      leftImageUrl: imageUrls.leftImageUrl,
      rightImageUrl: imageUrls.rightImageUrl,
      modifiedDate: this.toDateString(this.getModelValue<Date | string>(record, 'EditTime')),
      matchedDisabilityTypeCodes: this.getModelValue<string>(record, 'MatchedDisabilityTypeCodes') || '',
      computedMultipleDisabilityScore: this.getModelValue<string>(record, 'ComputedMultipleDisabilityScore') || null,
      selectedServiceMode: meta.selectedServiceMode || '',
      matchedTreatmentItem: meta.matchedTreatmentItem || ''
    };
  }

  private mapOptimizedToFullFormData(
    optimized: DentalDisabilityFormDataOptimized,
    record: PatientDisabilityDto
  ): DentalDisabilityFormData {
    const meta = this.parseRegisteries(this.getModelValue<string>(record, 'Registeries'));
    const scoreSeverityLevels = this.buildSeverityLevelsFromScores(optimized.disabilityScores || '');
    const metaSeverityLevels = this.isPlainObject(meta.severityLevels) ? meta.severityLevels : {};
    const oldDisabilityCategories = Array.isArray(meta.oldDisabilityCategories) && meta.oldDisabilityCategories.length > 0
      ? meta.oldDisabilityCategories
      : Object.keys(scoreSeverityLevels);
    const treatmentItems = Array.isArray(meta.treatmentItems)
      ? meta.treatmentItems
      : this.splitCsv(optimized.specialRequirements || '');

    return {
      patientNo: optimized.patientNo,
      patientName: optimized.patientName,
      patientCId: optimized.patientCId,
      disabilityCategories: this.splitCsv(optimized.disabilityCategories || ''),
      oldDisabilityCategories: oldDisabilityCategories,
      severityLevels: Object.keys(metaSeverityLevels).length > 0 ? metaSeverityLevels : scoreSeverityLevels,
      memo: '',
      treatmentItems: treatmentItems,
      specialRequirements: optimized.specialRequirements || '',
      leftImageUrl: optimized.leftImageUrl || '',
      rightImageUrl: optimized.rightImageUrl || '',
      modifiedDate: optimized.modifiedDate || '',
      matchedTreatmentItems: optimized.matchedTreatmentItem ? [optimized.matchedTreatmentItem] : [],
      matchedDisabilityTypeCodes: this.splitCsv(optimized.matchedDisabilityTypeCodes || ''),
      selectedServiceMode: optimized.selectedServiceMode || '',
      quickSelectTags: [],
      computedMultipleDisabilityScore: this.parseComputedScore(optimized.computedMultipleDisabilityScore || '')
    };
  }

  private buildRegisteries(formData: DentalDisabilityFormDataOptimized): string {
    const fullData = this.formDataSubject.value;
    const meta: any = {
      selectedServiceMode: formData.selectedServiceMode || '',
      matchedTreatmentItem: formData.matchedTreatmentItem || ''
    };

    if (fullData && fullData.patientNo === formData.patientNo) {
      meta.oldDisabilityCategories = fullData.oldDisabilityCategories || [];
      meta.severityLevels = fullData.severityLevels || {};
      meta.treatmentItems = fullData.treatmentItems || [];
    }

    let json = JSON.stringify(meta);
    if (json.length <= 1000) return json;

    delete meta.treatmentItems;
    json = JSON.stringify(meta);
    if (json.length <= 1000) return json;

    delete meta.oldDisabilityCategories;
    delete meta.severityLevels;
    return this.trimToColumn(JSON.stringify(meta), 1000);
  }

  private parseRegisteries(registeries?: string): any {
    if (!registeries) return {};
    try {
      const parsed = JSON.parse(registeries);
      return this.isPlainObject(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }

  private buildPersistedImageUrl(formData: DentalDisabilityFormDataOptimized): string {
    const left = formData.leftImageUrl || '';
    const right = formData.rightImageUrl || '';
    const canPersist = (url: string) => !!url && !url.startsWith('data:') && url.length <= 250;

    if (canPersist(left) && canPersist(right)) {
      const paired = JSON.stringify({ leftImageUrl: left, rightImageUrl: right });
      if (paired.length <= 250) return paired;
    }

    if (canPersist(left)) return left;
    if (canPersist(right)) return right;
    return '';
  }

  private parsePersistedImageUrl(imageUrl?: string): { leftImageUrl: string; rightImageUrl: string } {
    if (!imageUrl) {
      return { leftImageUrl: '', rightImageUrl: '' };
    }

    try {
      const parsed = JSON.parse(imageUrl);
      if (this.isPlainObject(parsed)) {
        return {
          leftImageUrl: parsed.leftImageUrl || '',
          rightImageUrl: parsed.rightImageUrl || ''
        };
      }
    } catch {
      // Persisted legacy value is treated as the left image URL.
    }

    return { leftImageUrl: imageUrl, rightImageUrl: '' };
  }

  private buildSeverityLevelsFromScores(disabilityScores: string): { [key: string]: string } {
    const severityLevels: { [key: string]: string } = {};
    this.splitCsv(disabilityScores).forEach(score => {
      const parsed = this.parseScore(score);
      if (parsed) {
        severityLevels[parsed.code] = parsed.severity;
      }
    });
    return severityLevels;
  }

  private parseComputedScore(score: string): { code: string; severity: 'mild' | 'moderate' | 'severe' | 'profound' | null } | null {
    const parsed = this.parseScore(score);
    return parsed ? { code: parsed.code, severity: parsed.severity } : null;
  }

  private parseScore(score: string): { code: string; severity: 'mild' | 'moderate' | 'severe' | 'profound' } | null {
    if (!score) return null;
    const idx = score.lastIndexOf('.');
    if (idx <= 0 || idx >= score.length - 1) return null;

    const severityMap: { [key: string]: 'mild' | 'moderate' | 'severe' | 'profound' } = {
      '1': 'mild',
      '2': 'moderate',
      '3': 'severe',
      '4': 'profound'
    };
    const severity = severityMap[score.substring(idx + 1)];
    if (!severity) return null;

    return {
      code: score.substring(0, idx),
      severity
    };
  }

  private splitCsv(value: string): string[] {
    return value ? value.split(',').map(x => x.trim()).filter(x => x) : [];
  }

  private hasOptimizedData(formData: DentalDisabilityFormDataOptimized | null): boolean {
    return !!(formData &&
      ((formData.disabilityScores && formData.disabilityScores.trim().length > 0) ||
       (formData.disabilityCategories && formData.disabilityCategories.trim().length > 0)));
  }

  private trimToColumn(value: string | null | undefined, maxLength: number): string {
    const text = value || '';
    if (text.length <= maxLength) return text;
    console.warn('[dental-disability-memo] value exceeds DB column length and was truncated.', { maxLength, text });
    return text.substring(0, maxLength);
  }

  private toDateString(value: Date | string | undefined): string {
    if (!value) return '';
    return value instanceof Date ? value.toISOString() : value;
  }

  private getModelValue<T>(model: any, pascalKey: string): T | undefined {
    if (!model) return undefined;
    if (model[pascalKey] !== undefined && model[pascalKey] !== null) return model[pascalKey] as T;
    const camelKey = pascalKey.charAt(0).toLowerCase() + pascalKey.slice(1);
    return model[camelKey] as T;
  }

  private isPlainObject(value: any): boolean {
    return !!value && typeof value === 'object' && !Array.isArray(value);
  }

  private saveOptimizedToLocalStorage(formData: DentalDisabilityFormDataOptimized): void {
    const patNo = formData.patientNo || this.currentPatientNo;
    if (!patNo) return;
    try {
      localStorage.setItem(this.STORAGE_PREFIX_OPTIMIZED + patNo, JSON.stringify(formData));
      console.log('💾 [身障問卷] 已保存到 localStorage, key:', this.STORAGE_PREFIX_OPTIMIZED + patNo);
    } catch (error) {
      console.error('Failed to save optimized format to localStorage:', error);
    }
  }

  /**
   * 從 localStorage 恢復優化格式
   */
  private restoreOptimizedFromLocalStorage(patientNo: string): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_PREFIX_OPTIMIZED + patientNo);
      if (stored) {
        const formData = JSON.parse(stored) as DentalDisabilityFormDataOptimized;
        this.formDataOptimizedSubject.next(formData);
        console.log('📂 [身障問卷] 已從 localStorage 還原, key:', this.STORAGE_PREFIX_OPTIMIZED + patientNo, formData);
      } else {
        this.formDataOptimizedSubject.next(null);
      }
    } catch (error) {
      console.error('Failed to restore optimized format from localStorage:', error);
      this.formDataOptimizedSubject.next(null);
    }
  }

  /**
   * 清除當前患者的 localStorage
   */
  private clearLocalStorage(): void {
    if (!this.currentPatientNo) return;
    this.clearLocalStorageForPatient(this.currentPatientNo);
  }

  private clearLocalStorageForPatient(patientNo: string): void {
    if (!patientNo) return;
    try {
      localStorage.removeItem(this.STORAGE_PREFIX + patientNo);
      localStorage.removeItem(this.STORAGE_PREFIX_OPTIMIZED + patientNo);
    } catch (error) {
      console.error('Failed to clear localStorage:', error);
    }
  }

  /**
   * 切換到指定患者並從 localStorage 載入先前保存的身障資料
   * @param patientNo 患者編號
   * @param emitFormSaved 是否觸發 formSaved$ 事件（用於重新計算加成）
   * @returns 是否有載入到先前保存的資料
   */
  loadForPatient(patientNo: string, emitFormSaved: boolean = false): boolean {
    this.currentPatientNo = patientNo;
    // 還原完整格式
    this.restoreFullFromLocalStorage(patientNo);
    // 還原優化格式
    let loaded: DentalDisabilityFormDataOptimized | null = null;
    try {
      const stored = localStorage.getItem(this.STORAGE_PREFIX_OPTIMIZED + patientNo);
      if (stored) {
        loaded = JSON.parse(stored) as DentalDisabilityFormDataOptimized;
        // emit 新物件，確保所有訂閱者都能收到
        this.formDataOptimizedSubject.next({ ...loaded });
        console.log('📂 [身障問卷] 已從 localStorage 還原並 emit 新物件, key:', this.STORAGE_PREFIX_OPTIMIZED + patientNo, loaded);
      } else {
        this.formDataOptimizedSubject.next(null);
      }
    } catch (error) {
      console.error('Failed to restore optimized format from localStorage:', error);
      this.formDataOptimizedSubject.next(null);
    }

    const hasData = !!(loaded && loaded.patientNo === patientNo &&
      ((loaded.disabilityScores && loaded.disabilityScores.trim().length > 0) ||
       (loaded.disabilityCategories && loaded.disabilityCategories.trim().length > 0)));

    if (hasData && emitFormSaved) {
      // 觸發 formSaved$ 讓訂閱者（exam fee grid 等）重新計算
      this.formSavedSubject.next({ ...loaded });
      console.log('🔄 [身障問卷] 已觸發 formSaved$ 重算, patientNo:', patientNo);
    }

    console.log('👤 [身障問卷] loadForPatient', { patientNo, hasData, emitFormSaved });
    return hasData;
  }

  /**
   * 重置當前患者的記憶快取（不刪除 localStorage 持久資料）
   * 專供換患者時呼叫，清空 BehaviorSubject 讓 UI 重置 highlight
   */
  resetForPatientSwitch(): void {
    this.formDataSubject.next(null);
    this.formDataOptimizedSubject.next(null);
    this.currentPatientNo = '';
  }

  /**
   * 解析優化格式的數據，將逗號分隔字串轉換為陣列（用於讀取和還原）
   */
  parseOptimizedFormData(formData: DentalDisabilityFormDataOptimized): {
    disabilityCategories: string[];
    disabilityScores: string[];
    specialRequirements: string[];
    matchedDisabilityTypeCodes: string[];
    matchedTreatmentItem: string;
  } {
    return {
      disabilityCategories: formData.disabilityCategories ? formData.disabilityCategories.split(', ').filter(x => x) : [],
      disabilityScores: formData.disabilityScores ? formData.disabilityScores.split(', ').filter(x => x) : [],
      specialRequirements: formData.specialRequirements ? formData.specialRequirements.split(', ').filter(x => x) : [],
      matchedDisabilityTypeCodes: formData.matchedDisabilityTypeCodes ? formData.matchedDisabilityTypeCodes.split(', ').filter(x => x) : [],
      matchedTreatmentItem: formData.matchedTreatmentItem || ''
    };
  }

  /**
   * ========== 身心障礙加成計算核心邏輯 ==========
   * @deprecated 計算邏輯已整合至 DentalAdditionApiService，建議改用 DentalAdditionApiService.calculateExamFeeMultiplier()
   * 保留此方法供向下相容，內部計算結果與 DentalAdditionApiService 一致
   *
   * @param examFeeCode 診察費代碼
   * @param formData 身障問卷優化數據
   * @returns 加成倍率（如 2.1, 1.9, 1.5, 1.3, 1.0）
   */
  calculateExamFeeMultiplier(examFeeCode: string, formData: DentalDisabilityFormDataOptimized | null): number {
    if (!formData || !formData.disabilityScores || !formData.selectedServiceMode) {
      return 1.0; // 無身障問卷數據，無加成
    }

    // 身障問卷沒有輸出特治代碼 → 不套用身障加成
    if (!formData.matchedTreatmentItem?.trim()) {
      return 1.0;
    }

    // 特殊診察費代碼處理
    const specialExamFees: { [key: string]: number } = {
      '00318C': 2.1,  // 固定2.1
      '00311C': 1.9,  // 固定1.9
      '00301C': 1.5,  // 固定1.5
      '00302C': 1.5,
      '00312C': 1.5,
      '00313C': 1.5,
      '00303C': 1.3,  // 固定1.3
      '00314C': 1.3
    };

    if (specialExamFees[examFeeCode]) {
      return specialExamFees[examFeeCode];
    }

    // 00128C 需根據障礙程度決定
    if (examFeeCode === '00128C') {
      return this.getMultiplierByDisabilityLevel(formData);
    }

    return 1.0; // 非身障診察費代碼
  }

  /**
   * @deprecated 計算邏輯已整合至 DentalAdditionApiService，建議改用 DentalAdditionApiService.calculateTreatmentMultiplier()
   * 根據身障問卷結果計算處置碼加成倍率
   * 排除：麻醉項目、牙周統合治療(91021C-91023C)
   *
   * @param treatmentCode 處置代碼
   * @param formData 身障問卷優化數據
   * @returns 加成倍率（如 2.1, 1.9, 1.5, 1.3, 1.0）
   */
  calculateTreatmentMultiplier(treatmentCode: string, formData: DentalDisabilityFormDataOptimized | null): number {
    if (!formData || !formData.disabilityScores || !formData.selectedServiceMode) {
      return 1.0; // 無身障問卷數據，無加成
    }

    // 身障問卷沒有輸出特治代碼 → 不套用身障加成
    if (!formData.matchedTreatmentItem?.trim()) {
      return 1.0;
    }

    if (formData.matchedTreatmentItem === 'LT') {
      return 1.2;
    }

    // 身心障礙處置排除代碼（麻醉、牙周統合、特殊項目）
    const exclusions = [
      '96001C',  // 麻醉
      '91021C', '91022C', '91023C',  // 牙周統合
      'P3601C', 'P7303C', '92131B', '92132B',  // 特殊項目
      'P30001', 'P30003'   // 身障服務（論次）
    ];

    if (exclusions.includes(treatmentCode)) {
      return 1.0; // 排除項目不加成
    }

    return this.getMultiplierByDisabilityLevel(formData);
  }

  /**
   * 根據障礙評分和醫療型態確定加成倍率
   * 依據「身心障礙牙醫處置加成與特定治療代號對照筆記.md」
   *
   * @param formData 身障問卷優化數據
   * @returns 加成倍率
   */
  private getMultiplierByDisabilityLevel(formData: DentalDisabilityFormDataOptimized): number {
    let scores = formData.disabilityScores?.split(',').map(s => s.trim()).filter(s => s) || [];

    // 居家模式下植物人輕度(09.1)排除，不參與加成計算
    if (formData.selectedServiceMode === 'home-care') {
      scores = scores.filter(s => s !== '09.1');
    }

    // 院內或醫缺巡迴下失能老人(16.1)排除，不參與加成計算
    if (formData.selectedServiceMode === 'in-clinic' || formData.selectedServiceMode === 'mobile-clinic') {
      scores = scores.filter(s => s !== '16.1');
    }

    // 檢查是否有符合各級別的障礙項目
    const has極重度 = scores.some(s => this.is極重度(s));
    const has失智或自閉 = scores.some(s => this.is失智或自閉(s));
    const has重度 = scores.some(s => this.is重度(s));
    const has中度 = scores.some(s => this.is中度(s));
    const has中度以上精神 = scores.some(s => this.is中度以上精神(s));
    const has發展遲緩 = scores.includes('16.2');
    const has腦傷脊髓中度肢體 = scores.some(s => this.is腦傷脊髓中度肢體(s));
    const has輕度 = scores.some(s => this.is輕度(s));
    const has失能老人 = scores.includes('16.1');

    // 依據優先級別判斷（由高到低）
    // 加 11 成 (+110%) = 倍率 2.1
    if (has極重度 || has失智或自閉) {
      return 2.1;
    }

    // 加 9 成 (+90%) = 倍率 1.9
    if (has重度) {
      return 1.9;
    }

    // 加 5 成 (+50%) = 倍率 1.5
    if (has中度 || has中度以上精神 || has發展遲緩 || has腦傷脊髓中度肢體) {
      return 1.5;
    }

    // 加 3 成 (+30%) = 倍率 1.3
    if (has輕度 || has失能老人) {
      return 1.3;
    }

    return 1.0; // 無符合加成資格
  }

  /**
   * 判斷是否為極重度（.4）非精神類別
   */
  private is極重度(score: string): boolean {
    // 排除精神類別（12）
    if (score.startsWith('12.')) return false;
    // 排除失智（10）、自閉（11），這兩個有特殊處理
    if (score.startsWith('10.') || score.startsWith('11.')) return false;

    return score.endsWith('.4');
  }

  /**
   * 判斷是否為失智症（10）或自閉症（11）任何等級
   */
  private is失智或自閉(score: string): boolean {
    return score.startsWith('10.') || score.startsWith('11.');
  }

  /**
   * 判斷是否為重度（.3）非精神類別
   */
  private is重度(score: string): boolean {
    // 排除精神類別（12）
    if (score.startsWith('12.')) return false;
    // 排除失智（10）、自閉（11）
    if (score.startsWith('10.') || score.startsWith('11.')) return false;

    return score.endsWith('.3');
  }

  /**
   * 判斷是否為中度（.2）非精神類別
   */
  private is中度(score: string): boolean {
    // 排除精神類別（12）
    if (score.startsWith('12.')) return false;
    // 排除失智（10）、自閉（11）
    if (score.startsWith('10.') || score.startsWith('11.')) return false;
    // 排除腦傷/脊髓損傷的中度肢體障礙（有特殊處理）
    if (score === '05-cerebral.2') return false;

    return score.endsWith('.2');
  }

  /**
   * 判斷是否為中度以上精神疾病（12.2, 12.3, 12.4）
   */
  private is中度以上精神(score: string): boolean {
    return score === '12.2' || score === '12.3' || score === '12.4';
  }

  /**
   * 判斷是否為腦傷/脊髓損傷之中度肢體障礙（05-cerebral.2）
   * 腦性麻痺已改為視同一般非精神類別，不再特殊處理
   */
  private is腦傷脊髓中度肢體(score: string): boolean {
    return score === '05-cerebral.2';
  }

  /**
   * 判斷是否為輕度（.1）符合資格類別
   */
  private is輕度(score: string): boolean {
    // 排除精神類別（12）
    if (score.startsWith('12.')) return false;
    // 排除失智（10）、自閉（11）
    if (score.startsWith('10.') || score.startsWith('11.')) return false;

    return score.endsWith('.1');
  }

  /**
   * 取得當前身障問卷的支付成數（用於申報）
   * @param formData 身障問卷優化數據
   * @returns 支付成數字串（如 "2.1", "1.9", "1.5", "1.3"）
   */
  getPaymentRatio(formData: DentalDisabilityFormDataOptimized | null): string {
    const multiplier = this.getMultiplierByDisabilityLevel(formData || {} as any);
    return multiplier.toFixed(1);
  }
}
